import classnames from "classnames";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import quoteService from "services/quote.service";
import { PlusIcon } from "components/assets/Icons";
import Button, { COLOR, SIZE, STYLE } from "components/Button";
import CallDetailModal from "components/CallDetailModal";
import Modal from "components/Modal";

import styles from "./AvailableTrucks.module.scss";

const AvailableTrucks = ({
  rfqData,
  condition,
  setShowModal,
  accepted,
  setAvailableData,
  availableData,
  showDetailedInfo,
  selectedBid,
  state,
  onAICallClick,
}) => {
  const availableTrucks = rfqData?.availableTrucks;
  const custom = useSelector((state) => state.customization);
  const permissions = useSelector((state) => state.permissions);
  const [open, setOpen] = useState({ element: null, state: false, id: "" });
  const [details, setDetails] = useState({});
  const [activeTab, setActiveTab] = useState(availableTrucks?.defaultType || "All");
  const [viewedItems, setViewedItems] = useState([]);
  const [callsData, setCallsData] = useState({});
  const [callDetailModal, setCallDetailModal] = useState({ open: false, callId: null });

  const handleClick = (index, id, e) => {
    setOpen({
      element: id,
      state: open.element === id ? !open.state : true,

      id,
    });
    setDetails({});

    if (!viewedItems.includes(id)) {
      setViewedItems([...viewedItems, id]);
    }
  };

  useEffect(() => {
    if (showDetailedInfo) {
      const fetchBidDetails = async () => {
        try {
          const data = await quoteService.getBidsDetails(selectedBid);
          showDetailedInfo && setDetails(data);
        } catch (error) {
          console.error("Error getting data:", error);
        }
      };
      fetchBidDetails();
    }
  }, [selectedBid, showDetailedInfo]);

  useEffect(() => {
    open.state &&
      (state?.dispatched
        ? quoteService.getTruckDetailsDispatched(open.id).then(
            ({ data }) => {
              setDetails(data);
            },
            ({ response }) => {
              console.error(response);
            },
          )
        : quoteService.getTruckDetails(open.id).then(
            ({ data }) => {
              setDetails(data);
            },
            ({ response }) => {
              console.error(response);
            },
          ));
  }, [open, state?.dispatched]);

  useEffect(() => {
    setOpen({ element: null, state: false, id: "" });
    setDetails({});
  }, [availableTrucks]);

  useEffect(() => {
    if (availableTrucks?.defaultType) {
      setActiveTab(availableTrucks.defaultType);
    }
  }, [availableTrucks]);

  useEffect(() => {
    let interval = null;

    const fetchCallsData = async () => {
      if (rfqData?.id) {
        try {
          const data = await quoteService.getRFQCalls(rfqData.id);
          setCallsData(data);
        } catch (error) {
          console.error('Error fetching calls data:', error);
        }
      }
    };

    if (rfqData?.id && permissions?.ai_calling) {
      fetchCallsData();
      interval = setInterval(fetchCallsData, 2000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [rfqData?.id, permissions?.ai_calling]);

  const {
    map_link,
    time_with_usko,
    loads_count,
    x_border,
    dock_high,
    hazardous,
    dims,
    lift_gate,
    team,
    payload,
    unit_number,
    unit_type,
    permanent_note,
    availability_note,
    location,
    available_date,
    link,
  } = details.truck || {};

  const { drivers, rfq } = details || {};

  const getCallData = (truckId) => {
    return callsData[truckId] || null;
  };

  const formatDuration = (durationMs) => {
    if (!durationMs) return '0';
    const seconds = Math.floor(durationMs / 1000);
    return `${seconds}s`;
  };

  const formatCallStatus = (status) => {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatCallSuccess = (success) => {
    if (success === null || success === undefined) return 'Pending';
    return success ? 'Yes' : 'No';
  };

  const getStatusClass = (status) => {
    if (!status) return styles.callStatusButton;

    switch (status.toLowerCase()) {
      case 'registered':
        return `${styles.callStatusButton} ${styles.statusRegistered}`;
      case 'not_connected':
        return `${styles.callStatusButton} ${styles.statusNotConnected}`;
      case 'ongoing':
        return `${styles.callStatusButton} ${styles.statusOngoing}`;
      case 'ended':
        return `${styles.callStatusButton} ${styles.statusEnded}`;
      case 'error':
        return `${styles.callStatusButton} ${styles.statusError}`;
      default:
        return `${styles.callStatusButton} ${styles.statusRegistered}`;
    }
  };

  const getSuccessClass = (success) => {
    if (success === null || success === undefined) return styles.successPending;
    return success ? styles.successYes : styles.successNo;
  };

  const getContactInfo = (item) => {
    // Prioritize dispatchers over drivers
    if (item?.dispatchers && item.dispatchers.length > 0) {
      return {
        contacts: item.dispatchers,
        type: 'dispatcher'
      };
    } else if (item?.drivers && item.drivers.length > 0) {
      return {
        contacts: item.drivers,
        type: 'driver'
      };
    }
    return {
      contacts: [],
      type: 'none'
    };
  };

  const handleCallDetailClick = (callId, e) => {
    e.stopPropagation();
    if (callId) {
      setCallDetailModal({ open: true, callId });
    }
  };

  const fullTruckInfo = (item, index, custom) => {
    return (
      <>
        <div className={styles.main}>
          <div className={styles.cell}>
            <Link
              to={!showDetailedInfo ? item?.link : link}
              className={styles.link}
              target="_blank"
              rel="noreferrer"
            >
              #{!showDetailedInfo ? item?.unit : unit_number}
            </Link>
          </div>
          <div
            className={styles.cell}
            style={{ display: "flex", flexDirection: "column" }}
          >
            {(() => {
              if (!showDetailedInfo) {
                const contactInfo = getContactInfo(item);
                return contactInfo.contacts?.map(({ name }, index) => (
                  <span key={index}>
                    {name.length >= (condition ? 8 : 7)
                      ? name.replace("_", " ").slice(0, condition ? 8 : 7) + "..."
                      : name.replace("_", " ")}
                  </span>
                ));
              } else {
                return drivers?.map(({ name }, index) => (
                  <span key={index}>
                    {name.length >= (condition ? 8 : 7)
                      ? name.replace("_", " ").slice(0, condition ? 8 : 7) + "..."
                      : name.replace("_", " ")}
                  </span>
                ));
              }
            })()}
          </div>
          <div
            className={styles.cell}
            style={{ display: "flex", flexDirection: "column" }}
          >
            {(() => {
              if (!showDetailedInfo) {
                const contactInfo = getContactInfo(item);
                return contactInfo.contacts?.map(({ phone, phone_number }, index) => (
                  <span key={2 + index}>{phone || phone_number}</span>
                ));
              } else {
                return drivers?.map(({ phone, phone_number }, index) => (
                  <span key={2 + index}>{phone_number || phone}</span>
                ));
              }
            })()}
          </div>
          <div className={styles.cell}>
            <Link
              to={!showDetailedInfo ? item?.map_link : map_link}
              className={styles.link}
              target="_blank"
              rel="noreferrer"
            >
              {!showDetailedInfo ? Math.round(item?.miles_out) : rfq?.miles}
            </Link>
          </div>
          {!showDetailedInfo && permissions?.ai_calling && (
            <>
              <div className={styles.cell}>
                <button
                  className={styles.aiButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onAICallClick) {
                      onAICallClick(rfqData.id, item?.id);
                    }
                  }}
                >
                  AI
                </button>
              </div>
              <div className={styles.cell}>
                {(() => {
                  const callData = getCallData(item?.id);
                  return callData ? (
                    <button
                      className={getStatusClass(callData.call_status)}
                      onClick={(e) => handleCallDetailClick(callData.id, e)}
                      title="Click to view call details"
                    >
                      {formatCallStatus(callData.call_status)}
                    </button>
                  ) : (
                    <span className={styles.emptyCell}>-</span>
                  );
                })()}
              </div>
              <div className={styles.cell}>
                {(() => {
                  const callData = getCallData(item?.id);
                  const driverRate = callData?.driver_rate || callData?.response_variables?.driver_rate;
                  return driverRate ? (
                    <button
                      className={styles.callInfoButton}
                      onClick={(e) => handleCallDetailClick(callData.id, e)}
                      title="Click to view call details"
                    >
                      ${driverRate}
                    </button>
                  ) : (
                    <span className={styles.emptyCell}>-</span>
                  );
                })()}
              </div>
              <div className={styles.cell}>
                {(() => {
                  const callData = getCallData(item?.id);
                  return callData ? (
                    <button
                      className={`${styles.callInfoButton} ${getSuccessClass(callData.call_successful)}`}
                      onClick={(e) => handleCallDetailClick(callData.id, e)}
                      title="Click to view call details"
                    >
                      {formatCallSuccess(callData.call_successful)}
                    </button>
                  ) : (
                    <span className={styles.emptyCell}>-</span>
                  );
                })()}
              </div>
            </>
          )}
          {!showDetailedInfo && (
            <button
              className={styles.button}
              onClick={(e) => handleClick(index, item?.id, e)}
            >
              <PlusIcon />
            </button>
          )}
        </div>
        <div
          className={styles.additional}
          style={{
            opacity: `${showDetailedInfo ? 1 : ""}`,
            visibility: `${showDetailedInfo ? "visible" : ""}`,
          }}
        >
          {(() => {
            const dispatchers = !showDetailedInfo ? item?.dispatchers : details?.dispatchers;
            if (dispatchers && dispatchers.length > 0) {
              return (
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Dispatchers</h4>
                  {dispatchers.map(({ name, phone }, index) => (
                    <span className={styles.row} key={index}>
                      <span>{name}</span>
                      <span>{phone}</span>
                    </span>
                  ))}
                </div>
              );
            }
            return null;
          })()}

          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Drivers</h4>
            {(!showDetailedInfo ? item?.drivers : drivers)?.map(
              ({ name, phone, phone_number }, index) => (
                <span className={styles.row} key={0 + index}>
                  <span>{name}</span>
                  <span>{!showDetailedInfo ? phone : phone_number}</span>
                </span>
              ),
            )}
          </div>

          <div className={styles.section}>
            <div className={styles.row}>
              <span className={styles.title}>Available Date/Time:</span>
              {(!showDetailedInfo ? details?.available_date : available_date) ?? "-"}
            </div>
            <div className={styles.row}>
              <span className={styles.title}>Location:</span>
              {(!showDetailedInfo ? details?.location : location) ?? "-"}
            </div>
            <div className={styles.row}>
              <span className={styles.title}>Availability Note:</span>
              {(!showDetailedInfo ? details?.availability_note : availability_note) ??
                "-"}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.row}>
              <div className={styles.column}>
                <span className={styles.title}>Unit Type:</span>
                {(!showDetailedInfo ? details?.unit_type : unit_type) ?? "-"}

                <span className={styles.title}>Payload:</span>
                {(!showDetailedInfo ? details?.payload : payload) ?? "-"}

                <span className={styles.title}>Team:</span>
                {(!showDetailedInfo ? details?.team : team)
                  ? (!showDetailedInfo ? details?.team : team)
                    ? "true"
                    : "false"
                  : "-"}

                <span className={styles.title}>Lift Gate:</span>
                {(!showDetailedInfo ? details?.lift_gate : lift_gate)
                  ? (!showDetailedInfo ? details?.lift_gate : lift_gate)
                    ? "true"
                    : "false"
                  : "-"}
              </div>
              <div className={styles.column}>
                <span className={styles.title}>Dims:</span>
                {(!showDetailedInfo ? details?.dims : dims) ?? "-"}

                <span className={styles.title}>Hazardous:</span>
                {(!showDetailedInfo ? details?.hazardous : hazardous)
                  ? (!showDetailedInfo ? details?.hazardous : hazardous)
                    ? "true"
                    : "false"
                  : "-"}

                <span className={styles.title}>Dock High:</span>
                {(!showDetailedInfo ? details?.dock_high : dock_high)
                  ? (!showDetailedInfo ? details?.dock_high : dock_high)
                    ? "true"
                    : "false"
                  : "-"}

                <span className={styles.title}>XBorder:</span>
                {(!showDetailedInfo ? details?.x_border : x_border)
                  ? (!showDetailedInfo ? details?.x_border : x_border)
                    ? "true"
                    : "false"
                  : "-"}
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.row}>
              <div className={styles.column}>
                <span className={styles.title}>Loads:</span>
                {(!showDetailedInfo ? details?.loads_count : loads_count) ?? "-"}
              </div>
              <div className={styles.column}>
                <span className={styles.title}>
                  With {custom?.customer_name ? custom?.customer_name : "Company"}:
                </span>
                {(!showDetailedInfo ? details?.time_with_usko : time_with_usko) ?? "-"}
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.row}>
              <span className={styles.title}>Permanent Note:</span>
              {(!showDetailedInfo ? details?.permanent_note : permanent_note) ?? "-"}
            </div>
          </div>

          {(accepted || (!rfqData.isFirstShown && rfqData.show_no_options_and_bid)) && (
            <div className={styles.section}>
              <Button
                children="BID"
                size={SIZE.MIDDLE}
                color={COLOR.LIGHT}
                styles={STYLE.SECONDARY}
                className={styles.bidButton}
                onClick={() => {
                  setShowModal(true);
                  setAvailableData({ ...availableData, item });
                }}
              />
            </div>
          )}
        </div>
      </>
    );
  };

  useEffect(() => {
    setOpen({ element: null, state: false, id: "" });
    setDetails({});
  }, [availableTrucks]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setOpen({ element: null, state: false, id: "" });
  };

  const allTrucks = availableTrucks?.data
    ? Object.values(availableTrucks.data).flat()
    : [];

  return (
    <>
      {!showDetailedInfo && (
        <div className={styles.tabsContainer}>
          {" "}
          <button
            type="button"
            onClick={() => handleTabChange("All")}
            className={`${styles.tabsItem} ${activeTab === "All" && styles.tabsActive}`}
          >
            All:<span>{allTrucks.length}</span>
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("sprinterVan")}
            className={`${styles.tabsItem} ${
              activeTab === "sprinterVan" && styles.tabsActive
            }`}
          >
            SV:<span>{availableTrucks?.data?.sprinterVan?.length || 0}</span>
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("smallStraight")}
            className={`${styles.tabsItem} ${
              activeTab === "smallStraight" && styles.tabsActive
            }`}
          >
            SS:<span>{availableTrucks?.data?.smallStraight?.length || 0}</span>
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("largeStraight")}
            className={`${styles.tabsItem} ${
              activeTab === "largeStraight" && styles.tabsActive
            }`}
          >
            LS:<span>{availableTrucks?.data?.largeStraight?.length || 0}</span>
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("reefer")}
            className={`${styles.tabsItem} ${
              activeTab === "reefer" && styles.tabsActive
            }`}
          >
            RF:<span>{availableTrucks?.data?.reefer?.length || 0}</span>
          </button>
        </div>
      )}
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.cell}>Unit #</div>
          <div className={styles.cell}>Name</div>
          <div className={styles.cell}>Phone Number</div>
          <div className={styles.cell}>Miles</div>
          {!showDetailedInfo && permissions?.ai_calling && (
            <>
              <div className={styles.cell}>AI</div>
              <div className={styles.cell}>Status</div>
              <div className={styles.cell}>Rate</div>
              <div className={styles.cell}>Success</div>
            </>
          )}
          <div className={styles.cell}></div>
        </div>
        {!showDetailedInfo ? (
          <div className={styles.list}>
            {activeTab === "All" ? (
              Array.isArray(allTrucks) && allTrucks.length > 0 ? (
                allTrucks.map((item, index) => {
                  return (
                    <div
                      className={classnames(
                        styles.item,
                        viewedItems.includes(item.id) ? styles.viewed : "",
                        open.element === item.id && open.state ? styles.open : "",
                      )}
                      key={index}
                    >
                      {fullTruckInfo(item, index, custom)}
                    </div>
                  );
                })
              ) : (
                <p className={styles.noData}></p>
              )
            ) : Array.isArray(availableTrucks?.data?.[activeTab]) &&
              availableTrucks.data[activeTab].length > 0 ? (
              availableTrucks.data[activeTab].map((item, index) => {
                return (
                  <div
                    className={classnames(
                      styles.item,
                      viewedItems.includes(item.id) ? styles.viewed : "",
                      open.element === item.id && open.state ? styles.open : "",
                    )}
                    key={index}
                  >
                    {fullTruckInfo(item, index, custom)}
                  </div>
                );
              })
            ) : (
              <p className={styles.noData}></p>
            )}
          </div>
        ) : (
          fullTruckInfo()
        )}
      </div>

      {callDetailModal.open && (
        <Modal
          onClose={() => setCallDetailModal({ open: false, callId: null })}
          hideCloseBtn={true}
          className={styles.callDetailModal}
        >
          <CallDetailModal
            callId={callDetailModal.callId}
            onClose={() => setCallDetailModal({ open: false, callId: null })}
          />
        </Modal>
      )}
    </>
  );
};

export default AvailableTrucks;
